import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * API service for conversation management
 * Handles chat conversations and message operations
 */
export const ConversationApi = {
  /**
   * Create a new conversation
   * @param {Object} conversationData - Conversation data
   * @returns {Promise<Response>} Created conversation
   */
  createConversation: async (conversationData) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(conversationData),
    });
  },

  /**
   * Get all conversations for a user
   * @param {string|number} userId - User ID
   * @param {Object} options - Pagination options (page, size)
   * @returns {Promise<Response>} User conversations
   */
  getUserConversations: async (userId, options = {}) => {
    const { page = 0, size = 20 } = options;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/user/${userId}?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Get a specific conversation by ID (metadata only, no messages)
   * @param {string|number} conversationId - Conversation ID
   * @returns {Promise<Response>} Conversation metadata
   */
  getConversation: async (conversationId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * ✅ NEW: Get conversation messages with pagination
   * @param {string|number} conversationId - Conversation ID
   * @param {Object} options - Pagination options (page, size)
   * @returns {Promise<Response>} Messages with pagination info
   */
  getConversationMessages: async (conversationId, options = {}) => {
    const { page = 0, size = 10 } = options;
    const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/${conversationId}/messages?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
  },

  /**
   * Update conversation title
   * @param {string|number} conversationId - Conversation ID
   * @param {string} title - New title
   * @returns {Promise<Response>} Updated conversation
   */
  updateConversationTitle: async (conversationId, title) => {
    // PATCH the conversation resource directly (no /title suffix)
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
  },

  /**
   * Update conversation metadata (title, attachedEndeavorIds, etc.)
   * @param {string|number} conversationId - Conversation ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Response>} Updated conversation
   */
  updateConversation: async (conversationId, updates) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
  },

  /**
   * Delete a conversation
   * @param {string|number} conversationId - Conversation ID
   * @returns {Promise<Response>} Deletion result
   */
  deleteConversation: async (conversationId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
  }
};